/**
 * 내가 만드는 메일함 (migration 114, 2026-08-21) — 시안의 "내 메일함".
 *
 * ★ 서버는 **평평한 목록 + parentId** 를 준다. 나무로 접는 것은 여기서 한다 —
 *   순서·펼침 상태 같은 화면의 사정이 응답 모양에 스며들면 안 된다.
 * ★ 이름 바꾸기·하위 추가는 **모달 없이 그 줄에서** 한다. Enter 저장 · Esc 취소.
 * ★ 백업은 아직 없다 — 메뉴에 넣지 않는다. 있는데 안 되는 항목보다 없는 편이 낫다.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { ChevronDown, Folder, MoreVertical, Plus, Check, Edit3, Trash2, X } from '../../icons';
import { tMail } from '../../lib/mail-i18n';
import type { MailFolderNode } from '../../api/mail';

export interface FolderMenuAction {
  kind: 'read-all' | 'empty' | 'rename' | 'add-sub' | 'delete';
  folder: MailFolderNode;
}

interface MailFolderTreeProps {
  folders: MailFolderNode[];
  /** 지금 보고 있는 폴더 id (없으면 null). */
  activeId: string | null;
  /** 3단까지. 그보다 깊은 자리에서는 "하위 메일함 추가" 가 흐려진다. */
  maxDepth?: number;
  onPick: (folder: MailFolderNode) => void;
  onCreate: (name: string, parentId: string | null) => void;
  onRename: (id: string, name: string) => void;
  onAction: (action: FolderMenuAction) => void;
}

interface Row { node: MailFolderNode; depth: number }

/** 평평한 목록을 나무 순서로 편다(부모 바로 밑에 자식). */
function flatten(folders: MailFolderNode[], collapsed: Set<string>): Row[] {
  const byParent = new Map<string | null, MailFolderNode[]>();
  for (const f of folders) {
    const key = f.parentId;
    const list = byParent.get(key);
    if (list) list.push(f); else byParent.set(key, [f]);
  }
  const out: Row[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const n of byParent.get(parentId) ?? []) {
      out.push({ node: n, depth });
      if (!collapsed.has(n.id)) walk(n.id, depth + 1);
    }
  };
  walk(null, 1);
  return out;
}

export function MailFolderTree(props: MailFolderTreeProps) {
  const { folders, activeId, maxDepth = 3, onPick, onCreate, onRename, onAction } = props;
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [menuFor, setMenuFor] = useState<string | null>(null);
  /** 지금 입력칸을 이고 있는 자리 — 'new:<parentId|root>' 또는 'rename:<id>' */
  const [editing, setEditing] = useState<string | null>(null);
  const doneRef = useRef(false);

  const rows = useMemo(() => flatten(folders, collapsed), [folders, collapsed]);
  const hasChild = useMemo(() => new Set(folders.map((f) => f.parentId).filter(Boolean) as string[]), [folders]);

  const startInput = useCallback((key: string) => { doneRef.current = false; setEditing(key); setMenuFor(null); }, []);

  const commit = useCallback((key: string, value: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setEditing(null);
    const name = value.trim();
    if (!name) return;
    if (key.startsWith('new:')) {
      const parent = key.slice(4);
      onCreate(name, parent === 'root' ? null : parent);
    } else {
      onRename(key.slice(7), name);
    }
  }, [onCreate, onRename]);

  const inputFor = (key: string, initial = '') => (
    <input
      key={key}
      className="mail-folder-input"
      defaultValue={initial}
      autoFocus
      placeholder={tMail('mail.folder.name_placeholder')}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit(key, (e.target as HTMLInputElement).value);
        if (e.key === 'Escape') { doneRef.current = true; setEditing(null); }
      }}
      onBlur={(e) => commit(key, e.target.value)}
    />
  );

  return (
    <div className="mail-folder-tree">
      {/* 담는 그릇 — 자기 자신은 메일을 갖지 않는다. 여기서 맨 위 폴더를 만든다. */}
      <div className="mail-folder-root">
        <Folder size={15} aria-hidden className="mail-folder-ic" />
        <span className="mail-folder-root-name">{tMail('mail.folder.my')}</span>
        <button
          type="button"
          className="mail-folder-tool"
          aria-label={tMail('mail.folder.add')}
          title={tMail('mail.folder.add')}
          onClick={() => startInput('new:root')}
        >
          <Plus size={15} aria-hidden />
        </button>
      </div>

      {editing === 'new:root' && inputFor('new:root')}

      {rows.map(({ node, depth }) => (
        <div key={node.id}>
          {editing === `rename:${node.id}` ? (
            <div style={{ paddingLeft: `${depth * 14}px` }}>{inputFor(`rename:${node.id}`, node.name)}</div>
          ) : (
            <div
              className={`mail-folder-row${activeId === node.id ? ' is-active' : ''}${menuFor === node.id ? ' is-menu' : ''}`}
              style={{ paddingLeft: `${8 + depth * 14}px` }}
            >
              {hasChild.has(node.id) ? (
                <button
                  type="button"
                  className="mail-folder-twist"
                  aria-label={collapsed.has(node.id) ? tMail('mail.group.expand') : tMail('mail.group.collapse')}
                  onClick={() => setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (next.has(node.id)) next.delete(node.id); else next.add(node.id);
                    return next;
                  })}
                >
                  <ChevronDown
                    size={12}
                    aria-hidden
                    style={collapsed.has(node.id) ? { transform: 'rotate(-90deg)' } : undefined}
                  />
                </button>
              ) : <span className="mail-folder-twist" aria-hidden />}

              <button type="button" className="mail-folder-name" onClick={() => onPick(node)}>
                <Folder size={14} aria-hidden className="mail-folder-ic" />
                <span className="mail-folder-label">{node.name}</span>
              </button>

              {node.unread > 0 && <span className="mail-folder-badge">{node.unread}</span>}

              <span className="mail-folder-tools">
                <button
                  type="button"
                  className="mail-folder-tool"
                  aria-label={`${node.name} ${tMail('mail.folder.manage')}`}
                  aria-haspopup="menu"
                  onClick={() => setMenuFor((v) => (v === node.id ? null : node.id))}
                >
                  <MoreVertical size={15} aria-hidden />
                </button>
              </span>

              {menuFor === node.id && (
                <div className="mail-folder-menu" role="menu">
                  <p className="mail-folder-menu-h">{node.name}</p>
                  <button type="button" role="menuitem" onClick={() => { setMenuFor(null); onAction({ kind: 'read-all', folder: node }); }}>
                    <Check size={14} aria-hidden />{tMail('mail.folder.read_all')}
                  </button>
                  <button type="button" role="menuitem" onClick={() => startInput(`rename:${node.id}`)}>
                    <Edit3 size={14} aria-hidden />{tMail('mail.folder.rename')}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={depth >= maxDepth}
                    title={depth >= maxDepth ? tMail('mail.folder.too_deep') : undefined}
                    onClick={() => startInput(`new:${node.id}`)}
                  >
                    <Plus size={14} aria-hidden />{tMail('mail.folder.add_sub')}
                  </button>
                  <div className="mail-folder-menu-sep" />
                  <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuFor(null); onAction({ kind: 'empty', folder: node }); }}>
                    <Trash2 size={14} aria-hidden />{tMail('mail.folder.empty')}
                  </button>
                  <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuFor(null); onAction({ kind: 'delete', folder: node }); }}>
                    <X size={14} aria-hidden />{tMail('mail.folder.delete')}
                  </button>
                </div>
              )}
            </div>
          )}
          {editing === `new:${node.id}` && (
            <div style={{ paddingLeft: `${8 + (depth + 1) * 14}px` }}>{inputFor(`new:${node.id}`)}</div>
          )}
        </div>
      ))}
    </div>
  );
}
